import ApplicationController from '../../../../javascript/controllers/application_controller'
import { BarcodeDetector, prepareZXingModule } from 'barcode-detector/ponyfill'

const DETECT_INTERVAL_MS = 100
// A misread can yield a *different valid* EAN-13, so a single successful
// decode is not trustworthy — require the same value on consecutive frames.
const REQUIRED_CONSECUTIVE_READS = 3

/**
 * Validates an EAN-13 check digit.
 * @param {String} code - candidate barcode value
 * @returns {Boolean} true for a 13-digit string with a valid check digit
 */
export function validEan13(code) {
  if (!/^\d{13}$/.test(code)) return false

  const digits = code.split('').map(Number)
  const checksum = digits
    .slice(0, 12)
    .reduce((sum, digit, index) => sum + digit * (index % 2 === 0 ? 1 : 3), 0)

  return (10 - (checksum % 10)) % 10 === digits[12]
}

/**
 * An ISBN-13 is an EAN-13 in the Bookland prefixes 978/979.
 * @param {String} code - candidate barcode value
 * @returns {Boolean}
 */
export function isbn13(code) {
  return /^97[89]\d{10}$/.test(code) && validEan13(code)
}

/**
 * Barcode Scanner Controller
 *
 * Drives the camera inside the scanner modal: getUserMedia lifecycle, a
 * throttled EAN-13 detect loop (zxing-cpp WASM via barcode-detector), and
 * the camera → confirm state transition.
 *
 * Breadcrumbs:
 *   - Scanner/detector_created: Which decoder was selected (metadata.native: browser
 *     BarcodeDetector vs the zxing WASM ponyfill)
 *   - Scanner/camera_started: When the camera stream begins
 *   - Scanner/camera_unavailable: When getUserMedia fails for a user-side reason
 *   - Scanner/barcode_detected: When a stable ISBN is accepted
 *
 * Error Handling:
 *   - Reports unexpected camera/decoder failures to AppSignal
 *
 * @extends ApplicationController
 */
export default class extends ApplicationController {
  static targets = [
    'video',
    'cameraState',
    'confirmState',
    'isbnInput',
    'torchButton',
    'zoomButton',
    'viewfinder',
    'retryButton',
    'errorNotAllowed',
    'errorNotFound',
    'errorNotReadable',
    'errorGeneric',
  ]

  static values = {
    wasmUrl: String,
  }

  connect() {
    // This controller is mounted on the modal's <dialog>, which removes itself on every
    // close path (cancel/X/Escape) — and submit replaces the surrounding turbo frame —
    // so disconnect() is the single teardown that stops the camera.
    this.decoderFailed = false
    // Native-first: use the browser's own BarcodeDetector when it can read EAN-13, so the
    // ~1 MB zxing WASM is fetched only as a fallback (iOS Safari — our primary device —
    // plus Firefox and desktop Chrome on Windows/Linux). Resolved in parallel with the
    // camera so neither blocks the other; detectFrame() waits for this.detector and
    // startCamera() bails if the decoder failed to load.
    this.createDetector()
      .then((detector) => (this.detector = detector))
      .catch((error) => this.failDecoder(error))
    this.startCamera().catch((error) => this.reportError(error, 'Camera startup failed'))
  }

  // Prefer the browser's native BarcodeDetector, but only when it actually decodes
  // EAN-13: `'BarcodeDetector' in window` is not enough — desktop Chrome on Windows/Linux
  // exposes the API with no OS backend, so getSupportedFormats() returns []. Only the
  // fallback compiles the self-hosted zxing WASM, and prepareZXingModule is the call that
  // fetches the ~1 MB binary — so native-capable browsers never download it.
  async createDetector() {
    const formats = ['ean_13']

    if ('BarcodeDetector' in window) {
      const supported = await window.BarcodeDetector.getSupportedFormats()
      if (formats.every((format) => supported.includes(format))) {
        this.addBreadcrumb('Scanner', 'detector_created', { native: true })
        return new window.BarcodeDetector({ formats })
      }
    }

    // fireImmediately compiles the WASM now rather than on first detect(), so it is ready
    // before the first frame and a load/compile failure surfaces here — otherwise
    // barcode-detector's internal no-op catch swallows it silently.
    await prepareZXingModule({
      overrides: {
        locateFile: (path, prefix) => (path.endsWith('.wasm') ? this.wasmUrlValue : prefix + path),
      },
      fireImmediately: true,
    })
    this.addBreadcrumb('Scanner', 'detector_created', { native: false })
    return new BarcodeDetector({ formats })
  }

  // A decoder that never loads leaves the camera running but never decoding. Stop the
  // camera and show the recoverable generic error instead of a dead preview.
  failDecoder(error) {
    this.decoderFailed = true
    this.stopCamera()
    this.showError(this.errorGenericTarget)
    this.reportError(error, 'Barcode scanner decoder failed to load')
  }

  disconnect() {
    this.stopCamera()
  }

  async startCamera() {
    this.lastCode = null
    this.consecutiveReads = 0
    this.detecting = false

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } },
      })
    } catch (error) {
      this.showCameraError(error)
      return
    }

    // The modal can close while getUserMedia is awaiting — disconnect() then found no
    // stream to stop, so release the just-acquired tracks here.
    if (!this.element.isConnected) {
      this.stopCamera()
      return
    }

    // The decoder may have failed to load while getUserMedia was awaiting; don't show a
    // live preview over the error state the failure already rendered.
    if (this.decoderFailed) {
      this.stopCamera()
      return
    }

    this.videoTarget.srcObject = this.stream
    this.setupTorchButton()
    this.setupZoomButton()
    this.setViewfinderLocked(false)
    this.addBreadcrumb('Scanner', 'camera_started')
    this.detectTimer = setInterval(
      () =>
        this.detectFrame().catch((error) => this.reportError(error, 'Barcode detect loop failed')),
      DETECT_INTERVAL_MS,
    )
  }

  stopCamera() {
    if (this.detectTimer) clearInterval(this.detectTimer)
    this.detectTimer = null
    this.stream?.getTracks()?.forEach((track) => track.stop())
    this.stream = null
    if (this.hasVideoTarget) this.videoTarget.srcObject = null
  }

  async detectFrame() {
    if (this.detecting) return
    // The detector resolves asynchronously (native support check / WASM compile); skip
    // frames until it is ready. The interval keeps firing, so nothing is lost.
    if (!this.detector) return
    if (this.videoTarget.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return

    this.detecting = true
    let barcodes
    try {
      barcodes = await this.detector.detect(this.videoTarget)
    } catch (error) {
      this.stopCamera()
      this.showError(this.errorGenericTarget)
      this.reportError(error, 'Barcode detection failed')
      return
    } finally {
      this.detecting = false
    }

    // stopCamera() may have run while detect() was awaiting; the modal is
    // torn down, so the result must not reach accept().
    if (!this.stream) return

    const code = barcodes[0]?.rawValue
    if (!code || !isbn13(code)) {
      this.setViewfinderLocked(false)
      return
    }

    this.consecutiveReads = code === this.lastCode ? this.consecutiveReads + 1 : 1
    this.lastCode = code
    // Green viewfinder while the quality gate counts identical reads — visible
    // "got it" feedback that covers the 3-read latency before the confirm swap.
    this.setViewfinderLocked(true)

    if (this.consecutiveReads >= REQUIRED_CONSECUTIVE_READS) this.accept(code)
  }

  accept(code) {
    this.stopCamera()
    this.addBreadcrumb('Scanner', 'barcode_detected')

    this.isbnInputTarget.value = code
    this.showConfirmState()
    this.isbnInputTarget.focus()
  }

  restart() {
    this.showCameraState()
    this.hideErrors()
    // Re-attempt the decoder if it failed to load (e.g. a transient WASM fetch error) so
    // retry can recover, not just camera-permission errors.
    if (this.decoderFailed && !this.detector) {
      this.decoderFailed = false
      this.createDetector()
        .then((detector) => (this.detector = detector))
        .catch((error) => this.failDecoder(error))
    }
    this.startCamera().catch((error) => this.reportError(error, 'Camera restart failed'))
  }

  // The confirm state spans the body form and the footer buttons, so both are marked
  // with the confirmState target and toggled together.
  showConfirmState() {
    this.cameraStateTargets.forEach((el) => el.classList.add('hidden'))
    this.confirmStateTargets.forEach((el) => el.classList.remove('hidden'))
  }

  showCameraState() {
    this.confirmStateTargets.forEach((el) => el.classList.add('hidden'))
    this.cameraStateTargets.forEach((el) => el.classList.remove('hidden'))
  }

  toggleTorch() {
    const [track] = this.stream?.getVideoTracks() || []
    if (!track) return

    this.torchOn = !this.torchOn
    track.applyConstraints({ advanced: [{ torch: this.torchOn }] }).catch((error) => {
      // Roll back the optimistic flip so the button keeps reflecting the real torch state.
      this.torchOn = !this.torchOn
      this.reportError(error, 'Torch toggle failed')
    })
  }

  setupTorchButton() {
    this.torchOn = false
    const [track] = this.stream.getVideoTracks()

    if (track?.getCapabilities?.()?.torch) {
      this.torchButtonTarget.classList.remove('hidden')
    } else {
      this.torchButtonTarget.classList.add('hidden')
    }
  }

  setupZoomButton() {
    this.zoomLevel = 1
    const [track] = this.stream.getVideoTracks()
    const zoom = track?.getCapabilities?.()?.zoom

    if (zoom && zoom.max >= 2) {
      this.zoomButtonTarget.textContent = '1×'
      this.zoomButtonTarget.classList.remove('hidden')
    } else {
      this.zoomButtonTarget.classList.add('hidden')
    }
  }

  toggleZoom() {
    const [track] = this.stream?.getVideoTracks() || []
    if (!track) return

    const next = this.zoomLevel === 1 ? 2 : 1
    track
      .applyConstraints({ advanced: [{ zoom: next }] })
      .then(() => {
        this.zoomLevel = next
        // The modal can close while applyConstraints() is awaiting; skip the now-gone target
        // instead of throwing into the catch below as a spurious "Zoom toggle failed" report.
        if (!this.hasZoomButtonTarget) return
        // The badge shows the CURRENT level, like a camera app's lens badge.
        this.zoomButtonTarget.textContent = `${next}×`
      })
      .catch((error) => this.reportError(error, 'Zoom toggle failed'))
  }

  setViewfinderLocked(locked) {
    if (!this.hasViewfinderTarget) return

    this.viewfinderTarget.classList.toggle('text-green-400', locked)
    this.viewfinderTarget.classList.toggle('text-white/70', !locked)
  }

  showCameraError(error) {
    const target = {
      NotAllowedError: this.errorNotAllowedTarget,
      NotFoundError: this.errorNotFoundTarget,
      NotReadableError: this.errorNotReadableTarget,
    }[error.name]

    this.showError(target || this.errorGenericTarget)

    if (target) {
      // A user-side condition (denied permission, no camera), not an app error.
      this.addBreadcrumb('Scanner', 'camera_unavailable', { errorName: error.name })
    } else {
      this.reportError(error, 'Camera access failed')
    }
  }

  // Every error state must offer in-modal recovery: after the user fixes the
  // cause (e.g. re-allows camera access), retry restarts the camera without
  // closing and reopening the modal.
  showError(target) {
    this.hideErrors()
    target.classList.remove('hidden')
    this.retryButtonTarget.classList.remove('hidden')
  }

  hideErrors() {
    ;[
      this.errorNotAllowedTarget,
      this.errorNotFoundTarget,
      this.errorNotReadableTarget,
      this.errorGenericTarget,
      this.retryButtonTarget,
    ].forEach((el) => el.classList.add('hidden'))
  }
}
